import React from "react";
import { Collapse, SimpleForm as Form, Input, Switch, Radio, InputNumber } from '@whalecloud/fdx';
import { set } from "dot-prop-immutable";
import { toolFormAttr, toolCollapseAttr, getLocale } from "@utils/util";
import DataSettings from './DataSettings';
import ColumnSettings from './ColumnSettings';

const { Panel } = Collapse;

const valueKey = 'data.tableSettings';

const TableSettings = ({ element, updateElement }) => {
  const messages = getLocale();

  const { tableSettings = {}, formSettings = {}, settings = {} } = element.data;
  const { name, label, rules = [] } = formSettings;
  const {
    visible = true
  } = settings

  const {
    style = {},
    scroll = {},
    pagination,
    rowSelection,
    size,
    rowKey,
    bordered,
    tableLayout,
    hideLabel = false,
  } = tableSettings;

  const required = rules[0]?.required;
  const message = rules[0]?.message || messages['PAGEBUIDER_SETTINGS_REQUIRED_TABLE_TIPS'];
  const newRuleItem = {
    required,
    message
  };

  const initialValues = {
    name,
    label,
    visible,
    required,
    message,
    size,
    rowKey,
    bordered,
    tableLayout,
    rowSelection,
    pagination,
    hideLabel,
    'style.width': style.width,
    'style.height': style.height,
    'scroll.x': scroll.x,
    'scroll.y': scroll.y,
    'pagination.pageSize': pagination?.pageSize,
    'rowSelection.type': rowSelection?.type || 'checkbox',
    'rowSelection.fixed': rowSelection?.fixed,
    'pagination.total': pagination?.total
  }

  const onFormChange = (obj: Object) => {
    Object.keys(obj).forEach((key: string) => {
      let propName = `${valueKey}.${key}`;
      let value = obj[key];
      if (key === 'rowSelection') {
        value = obj[key] ? {} : null;
      }
      if (key === 'visible') {
        propName = `data.settings.${key}`
        value = obj[key] ? 'visible' : 'hidden';
      }
      if (['name', 'label'].includes(key)) {
        propName = `data.formSettings.${key}`;
      }

      if (['required', 'message'].includes(key)) {
        propName = 'data.formSettings.rules';
        newRuleItem[key] = obj[key];
        value = [newRuleItem];
      }
      const newElement = set(element, propName, value);
      updateElement({
        element: newElement
      });
    })
  }

  return (
    <Collapse {...toolCollapseAttr} defaultActiveKey={['1', '2']}>
      <Panel header={messages['PAGEBUIDER_SETTINGS']} key="1">
        <Form {...toolFormAttr} initialValues={initialValues} onValuesChange={onFormChange}>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_NAME']} name="name">
            <Input placeholder="Input the form field name" />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_LABEL_TEXT']} name="label">
            <Input placeholder="Input the form field label" />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_TABLE_KEY']} name="rowKey">
            <Input />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_AUTO_INITILIZE']} name="autoInitialize" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_VISIBLE']} name="visible" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_REQUIRED_ITEM']} name="required" valuePropName="checked">
            <Switch />
          </Form.Item>
          {rules[0]?.required && (
            <Form.Item label={messages['PAGEBUIDER_SETTINGS_MESSAGE']} name="message">
              <Input.TextArea />
            </Form.Item>
          )}
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_SIZE']} name="size">
            <Radio.Group size="small">
              <Radio.Button value="default">{messages['PAGEBUIDER_SETTINGS_DEFAULT']}</Radio.Button>
              <Radio.Button value="middle">{messages['PAGEBUIDER_SETTINGS_MIDDLE']}</Radio.Button>
              <Radio.Button value="small">{messages['PAGEBUIDER_SETTINGS_SMALL']}</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_WIDTH']} name="style.width">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_HEIGHT']} name="style.height">
            <InputNumber min={0} />
          </Form.Item>
          {style.width > 0 && <Form.Item label={messages['PAGEBUIDER_SETTINGS_SCROLLX']} name="scroll.x">
            <InputNumber min={0} />
          </Form.Item>}
          {style.height > 0 && <Form.Item label={messages['PAGEBUIDER_SETTINGS_SCROLLY']} name="scroll.y">
            <InputNumber min={0} max={style.height} />
          </Form.Item>}
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_BORDERED']} name="bordered" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_TABLE_LAYOUT']} name="tableLayout">
            <Radio.Group size="small">
              <Radio.Button value={undefined}>{messages['PAGEBUIDER_SETTINGS_UNSET']}</Radio.Button>
              <Radio.Button value="fixed">{messages['PAGEBUIDER_SETTINGS_FIXED']}</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_MULTIPLE']} name="rowSelection" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_HIDE_LABEL']} name="hideLabel" valuePropName="checked">
            <Switch />
          </Form.Item>
          {
            rowSelection && (
              <div style={{ width: '80%', marginLeft: '20%' }}>
                <Form.Item span={10} label={messages['PAGEBUIDER_SETTINGS_MODE']} name="rowSelection.type">
                  <Radio.Group size="small">
                    <Radio.Button value="checkbox">{messages['PAGEBUIDER_SETTINGS_CHECKBOX']}</Radio.Button>
                    <Radio.Button value="radio">{messages['PAGEBUIDER_SETTINGS_RADIO']}</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label={messages['PAGEBUIDER_SETTINGS_FIXED']} name="rowSelection.fixed" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>
            )
          }
          <Form.Item label={messages['PAGEBUIDER_SETTINGS_PAGINATION']} name="pagination" valuePropName="checked">
            <Switch />
          </Form.Item>
          {tableSettings.pagination && (
            <div style={{ width: '80%', marginLeft: '20%' }}>
              <Form.Item label={messages['PAGEBUIDER_SETTINGS_PAGE_TOTAL']} name="pagination.total">
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item label={messages['PAGEBUIDER_SETTINGS_PAGE_SIZE']} name="pagination.pageSize">
                <InputNumber min={0} />
              </Form.Item>
            </div>
          )}
        </Form>
      </Panel>
      <Panel header={messages['PAGEBUIDER_SETTINGS_FORM_TABS_DATA']} key="2">
        <DataSettings element={element} updateElement={updateElement} />
        <ColumnSettings updateElement={updateElement} />
      </Panel>
    </Collapse>
  );
};

export default TableSettings;
